import { ResponseStatus } from '../types';
import { legacyNegativeId, safeNegativeId } from '../utils/idUtils';
import { offlineDatabase, OfflineResponse } from './offlineDatabase';

export interface ResponseSyncGroup {
  response: OfflineResponse;
  duplicates: OfflineResponse[];
}

export interface ResolvedResponseSyncCandidate {
  response: OfflineResponse;
  realItemId: number | null;
}

export interface ResponseItemResolverDependencies {
  executeSql: typeof offlineDatabase.executeSql;
  getAllIdMappings: typeof offlineDatabase.getAllIdMappings;
}

function getResolverDependencies(
  dependencies?: Partial<ResponseItemResolverDependencies>
): ResponseItemResolverDependencies {
  return {
    executeSql: dependencies?.executeSql ?? offlineDatabase.executeSql.bind(offlineDatabase),
    getAllIdMappings: dependencies?.getAllIdMappings ?? offlineDatabase.getAllIdMappings.bind(offlineDatabase),
  };
}

export async function resolveResponseItemId(
  offlineResponse: Pick<OfflineResponse, 'reviewId' | 'itemId'>,
  dependencies?: Partial<ResponseItemResolverDependencies>
): Promise<number | null> {
  const deps = getResolverDependencies(dependencies);

  try {
    const result = await deps.executeSql(
      'SELECT id, temp_id, sync_status FROM offline_on_demand_items WHERE review_id = ?',
      [offlineResponse.reviewId]
    );

    let offlineItem: any | null = null;
    for (let index = 0; index < result.rows.length; index++) {
      const candidate = result.rows.item(index);
      const tempId = candidate?.temp_id;
      const itemId = Number(offlineResponse.itemId);
      const matchesStoredId = Number(candidate?.id) === itemId;
      const matchesSafeId = typeof tempId === 'string' && safeNegativeId(tempId) === itemId;
      const matchesLegacyId = typeof tempId === 'string' && legacyNegativeId(tempId) === itemId;
      const matchesSyncedId = typeof tempId === 'string' && tempId === `synced_${itemId}`;

      if (matchesStoredId || matchesSafeId || matchesLegacyId || matchesSyncedId) {
        offlineItem = candidate;
        break;
      }
    }

    if (!offlineItem) {
      return offlineResponse.itemId;
    }

    const tempId = offlineItem?.temp_id;

    if (typeof tempId === 'string' && tempId.startsWith('synced_')) {
      const serverId = Number(tempId.replace('synced_', ''));
      if (Number.isFinite(serverId)) {
        return serverId;
      }
    }

    if (tempId) {
      const mappings = await deps.getAllIdMappings();
      const itemMapping = mappings.find(
        mapping => mapping.tempId === tempId && mapping.entityType === 'item'
      );

      if (itemMapping) {
        return itemMapping.realId;
      }
    }

    if (
      offlineItem?.sync_status === 'pending' ||
      offlineItem?.sync_status === 'syncing' ||
      offlineItem?.sync_status === 'failed'
    ) {
      return null;
    }

    return offlineResponse.itemId;
  } catch (error) {
    console.warn('[OfflineResponseSyncPlanner] Failed to resolve response item ID:', error);
    return offlineResponse.itemId;
  }
}

export function getResponseSyncRank(response: Pick<OfflineResponse, 'status'>): number {
  switch (response.status) {
    case ResponseStatus.COMPLETED:
    case 'completed':
      return 3;
    case ResponseStatus.IN_PROGRESS:
    case 'in_progress':
      return 2;
    case ResponseStatus.NOT_STARTED:
    case 'not_started':
      return 1;
    default:
      return 0;
  }
}

export function getResponseUpdatedAtTime(
  response: Pick<OfflineResponse, 'updatedAt' | 'createdAt'>
): number {
  const value = response.updatedAt || response.createdAt;
  if (!value) {
    return 0;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

export function shouldUseResponseForSync(
  candidate: OfflineResponse,
  current: OfflineResponse
): boolean {
  const candidateRank = getResponseSyncRank(candidate);
  const currentRank = getResponseSyncRank(current);

  if (candidateRank !== currentRank) {
    return candidateRank > currentRank;
  }

  const candidateUpdatedAt = getResponseUpdatedAtTime(candidate);
  const currentUpdatedAt = getResponseUpdatedAtTime(current);

  if (candidateUpdatedAt !== currentUpdatedAt) {
    return candidateUpdatedAt > currentUpdatedAt;
  }

  return candidate.id > current.id;
}

export function buildResponseSyncGroups(
  realReviewId: number,
  candidates: ResolvedResponseSyncCandidate[]
): ResponseSyncGroup[] {
  const groups = new Map<string, ResponseSyncGroup>();

  for (const candidate of candidates) {
    const { response, realItemId } = candidate;
    const itemKey = realItemId == null
      ? `deferred:${response.itemId}:${response.id}`
      : String(realItemId);
    const groupKey = `${realReviewId}:${itemKey}`;
    const existingGroup = groups.get(groupKey);

    if (!existingGroup) {
      groups.set(groupKey, {
        response,
        duplicates: [],
      });
      continue;
    }

    if (shouldUseResponseForSync(response, existingGroup.response)) {
      existingGroup.duplicates.push(existingGroup.response);
      existingGroup.response = response;
    } else {
      existingGroup.duplicates.push(response);
    }
  }

  return Array.from(groups.values());
}
